import type { Column } from '@creditornot/cb-data-table';
import type { CourierWithExtraInfo } from 'common/hooks/useMergeCourierExtraInfo';
import type { CsvColumn } from 'common/utils/reportUtils';

import { useMemo } from 'react';

import { type CourierTransaction, TransactionType } from 'common/api/api-types';
import GreyOut from 'common/components/GreyOut';
import IdAndTeam from 'common/components/Table/IdAndTeam';
import Name from 'common/components/Table/Name';
import { isContractExpired } from 'common/utils/filterUtils';
import { idsCsv } from 'features/earnings/constants';

import AggregatedCell from './AggregatedCell';
import TaskRelated from './TaskRelated';
import TotalAmount from './TotalAmount';

const getTransactionByType = ({
  aggregatedTransactions,
  transactionType,
}: {
  aggregatedTransactions?: Array<CourierTransaction>;
  transactionType: TransactionType;
}) => aggregatedTransactions?.find(transaction => transaction.transactionType === transactionType);

export const calculateTaskRelated = ({
  includedTransactionTypes,
  transactions,
}: {
  includedTransactionTypes: Array<CourierTransaction['transactionType']>;
  transactions?: CourierTransaction[];
}) =>
  transactions?.reduce<number | undefined>((result, curr) => {
    if (includedTransactionTypes.includes(curr.transactionType)) {
      return (result || 0) + curr.amount;
    }

    return result;
  }, undefined);

export const calculateTotalEarnings = ({
  excludedTransactionTypes,
  transactions,
}: {
  excludedTransactionTypes: Array<CourierTransaction['transactionType']>;
  transactions?: CourierTransaction[];
}) =>
  transactions?.reduce((acc: number | undefined, { amount, transactionType }) => {
    if (
      TASK_RELATED_TRANSACTIONS.includes(transactionType) ||
      (isValidColumnId(transactionType) && !excludedTransactionTypes.includes(transactionType))
    ) {
      return (acc || 0) + amount;
    }
    return acc;
  }, undefined);

export const getTips = (aggregatedTransactions?: Array<CourierTransaction>) =>
  getTransactionByType({
    aggregatedTransactions,
    transactionType: 'transaction_type_tip',
  });

export const getAdjustments = (aggregatedTransactions?: Array<CourierTransaction>) =>
  getTransactionByType({
    aggregatedTransactions,
    transactionType: 'transaction_type_manual',
  });

export const getShiftGuarantee = (aggregatedTransactions?: Array<CourierTransaction>) =>
  getTransactionByType({
    aggregatedTransactions,
    transactionType: 'transaction_type_shift_guarantee',
  });

export const getBonusCost = (aggregatedTransactions?: Array<CourierTransaction>) =>
  getTransactionByType({
    aggregatedTransactions,
    transactionType: 'transaction_type_bonus_cost',
  });

export const getDphBonusCost = (aggregatedTransactions?: Array<CourierTransaction>) =>
  getTransactionByType({
    aggregatedTransactions,
    transactionType: 'transaction_type_dph_bonus_cost',
  });

export const getUnspecified = (aggregatedTransactions?: Array<CourierTransaction>) =>
  getTransactionByType({
    aggregatedTransactions,
    transactionType: 'transaction_type_unspecified',
  });

export const getUnknown = (aggregatedTransactions?: Array<CourierTransaction>) =>
  getTransactionByType({
    aggregatedTransactions,
    transactionType: 'unknown',
  });

export const TASK_RELATED_TRANSACTIONS: Array<CourierTransaction['transactionType']> = [
  'transaction_type_task_base_cost',
  'transaction_type_task_distance_cost',
  'transaction_type_task_pickup_distance_cost',
  'transaction_type_task_capability_cost',
  'transaction_type_upfront_pricing_adjustment',
  'transaction_type_pickup_estimated_execution_time_cost',
  'transaction_type_dropoff_estimated_execution_time_cost',
  'transaction_type_pickup_wait_time_cost',
  'transaction_type_new_lead_bonus_cost',
  'transaction_type_referrer_bonus_cost',
  'transaction_type_micro_task_cost',
];

export const defaultIds = {
  id: 'id',
  name: 'name',
  totalAmountWithoutTip: 'totalAmountWithoutTip',
  totalAmountWithTip: 'totalAmountWithTip',
  transaction_type_tip: 'transaction_type_tip',
  transaction_type_manual: 'transaction_type_manual',
  transaction_type_shift_guarantee: 'transaction_type_shift_guarantee',
  taskRelated: 'taskRelated',
  transaction_type_bonus_cost: 'transaction_type_bonus_cost',
  transaction_type_dph_bonus_cost: 'transaction_type_dph_bonus_cost',
  transaction_type_unspecified: 'transaction_type_unspecified',
  unknown: 'unknown',
};

export const EXCLUDED_TRANSACTIONS: Array<CourierTransaction['transactionType']> = [
  'transaction_type_payout',
];

export const EXCLUDED_TRANSACTIONS_WITHOUT_TIPS: Array<CourierTransaction['transactionType']> = [
  'transaction_type_tip',
  ...EXCLUDED_TRANSACTIONS,
];

const defaultCsvColumns: Array<CsvColumn> = [
  {
    id: idsCsv.onlyName,
    name: 'Name',
  },
  {
    id: idsCsv.phone,
    name: 'Phone number',
  },
  {
    id: idsCsv.email,
    name: 'Email',
  },
  {
    id: idsCsv.onlyId,
    name: 'Id',
  },
  {
    id: idsCsv.team,
    name: 'Team',
  },
  {
    id: idsCsv.maxWeeklyHours,
    name: 'Weekly maximum hours',
  },
  {
    id: defaultIds.totalAmountWithoutTip,
    name: 'Total Amount (w.o. tips)',
  },
  {
    id: defaultIds.totalAmountWithTip,
    name: 'Total Amount (w. tips)',
  },
  {
    id: defaultIds.transaction_type_tip,
    name: 'Tips',
  },
] as Array<Column<CourierWithExtraInfo, unknown>>;

const defaultColumns = [
  {
    alignContentVertical: 'top',
    id: defaultIds.name,
    name: 'Name',
    render: ({ record }) => (
      <GreyOut $enabled={isContractExpired(record)}>
        <Name name={record.preferredName} phone={record.phone} />
      </GreyOut>
    ),
    sortDirections: ['ascend', 'descend'],
  },

  {
    alignContentVertical: 'top',
    id: defaultIds.id,
    name: 'Id / Team',
    render: ({ record }) => (
      <GreyOut $enabled={isContractExpired(record)}>
        <IdAndTeam id={record.id} team={record.deliveryArea} />
      </GreyOut>
    ),

    sortDirections: ['ascend', 'descend'],
  },
  {
    alignContentVertical: 'top',
    id: defaultIds.totalAmountWithoutTip,
    name: 'Total Amount (w.o. tips)',
    render: ({ record }) => {
      return (
        <GreyOut $enabled={isContractExpired(record)}>
          <TotalAmount
            excludedTransactions={EXCLUDED_TRANSACTIONS_WITHOUT_TIPS}
            earnings={record._extra.earnings}
            title="totalAmountWithoutTip"
          />
        </GreyOut>
      );
    },
    sortDirections: ['ascend', 'descend'],
  },
  {
    alignContentVertical: 'top',
    id: defaultIds.totalAmountWithTip,
    name: 'Total Amount (w. tips)',
    render: ({ record }) => {
      return (
        <GreyOut $enabled={isContractExpired(record)}>
          <TotalAmount
            excludedTransactions={EXCLUDED_TRANSACTIONS}
            earnings={record._extra.earnings}
            title="totalAmountWithTip"
          />
        </GreyOut>
      );
    },
    sortDirections: ['ascend', 'descend'],
  },
  {
    alignContentVertical: 'top',
    id: defaultIds.transaction_type_tip,
    name: 'Tips',
    render: ({ record }) => {
      return (
        <GreyOut $enabled={isContractExpired(record)}>
          <AggregatedCell
            value={getTips(record._extra.earnings?.aggregatedTransactions)}
            title="transaction_type_tip"
          />
        </GreyOut>
      );
    },
    sortDirections: ['ascend', 'descend'],
  },
] as Array<Column<CourierWithExtraInfo, unknown>>;

const aggregatedTransactionColumns = [
  {
    alignContentVertical: 'top',
    id: defaultIds.transaction_type_manual,
    name: 'Adjustments',
    render: ({ record }) => {
      return (
        <GreyOut $enabled={isContractExpired(record)}>
          <AggregatedCell
            value={getAdjustments(record._extra.earnings?.aggregatedTransactions)}
            title="transaction_type_manual"
          />
        </GreyOut>
      );
    },
    sortDirections: ['ascend', 'descend'],
  },
  {
    alignContentVertical: 'top',
    id: defaultIds.transaction_type_shift_guarantee,
    name: 'Shift Guarantee',
    render: ({ record }) => {
      return (
        <GreyOut $enabled={isContractExpired(record)}>
          <AggregatedCell
            value={getShiftGuarantee(record._extra.earnings?.aggregatedTransactions)}
            title="transaction_type_shift_guarantee"
          />
        </GreyOut>
      );
    },
    sortDirections: ['ascend', 'descend'],
  },
  {
    alignContentVertical: 'top',
    id: defaultIds.taskRelated,
    name: 'Task Related',
    render: ({ record }) => {
      return (
        <GreyOut $enabled={isContractExpired(record)}>
          <TaskRelated
            transactions={TASK_RELATED_TRANSACTIONS}
            earnings={record._extra.earnings}
            title="taskRelated"
          />
        </GreyOut>
      );
    },
    sortDirections: ['ascend', 'descend'],
  },
  {
    alignContentVertical: 'top',
    id: defaultIds.transaction_type_bonus_cost,
    name: 'Bonus Cost',
    render: ({ record }) => {
      return (
        <GreyOut $enabled={isContractExpired(record)}>
          <AggregatedCell
            value={getBonusCost(record._extra.earnings?.aggregatedTransactions)}
            title="transaction_type_bonus_cost"
          />
        </GreyOut>
      );
    },
    sortDirections: ['ascend', 'descend'],
  },
  {
    alignContentVertical: 'top',
    id: defaultIds.transaction_type_dph_bonus_cost,
    name: 'DPH Bonus Cost',
    render: ({ record }) => {
      return (
        <GreyOut $enabled={isContractExpired(record)}>
          <AggregatedCell
            value={getDphBonusCost(record._extra.earnings?.aggregatedTransactions)}
            title="transaction_type_dph_bonus_cost"
          />
        </GreyOut>
      );
    },
    sortDirections: ['ascend', 'descend'],
  },
  {
    alignContentVertical: 'top',
    id: defaultIds.transaction_type_unspecified,
    name: 'Unspecified',
    render: ({ record }) => {
      return (
        <GreyOut $enabled={isContractExpired(record)}>
          <AggregatedCell
            value={getUnspecified(record._extra.earnings?.aggregatedTransactions)}
            title="transaction_type_unspecified"
          />
        </GreyOut>
      );
    },
    sortDirections: ['ascend', 'descend'],
  },
  {
    alignContentVertical: 'top',
    id: defaultIds.unknown,
    name: 'Unknown',
    render: ({ record }) => {
      return (
        <GreyOut $enabled={isContractExpired(record)}>
          <AggregatedCell
            value={getUnknown(record._extra.earnings?.aggregatedTransactions)}
            title="unknown"
          />
        </GreyOut>
      );
    },
    sortDirections: ['ascend', 'descend'],
  },
] as Array<Column<CourierWithExtraInfo, unknown>>;

export const isValidColumnId = (id: string): id is keyof typeof defaultIds => {
  return id in defaultIds;
};

export const isIdInGroupedTransactionColumns = (id: CourierTransaction['transactionType']) => {
  return TASK_RELATED_TRANSACTIONS.includes(id);
};

export const isSupportedTransactionTypes = (id: CourierTransaction['transactionType']) => {
  const result = TransactionType.safeParse(id);

  return result.success;
};

const convertToCsvColumn = (column: Column<CourierWithExtraInfo, unknown>) => ({
  id: column.id,
  name: column.name,
});

const useAggregatedColumns = (ids: Set<keyof typeof defaultIds>) => {
  const result = useMemo(() => {
    const additionalColumns = aggregatedTransactionColumns.filter(
      column => isValidColumnId(column.id) && ids.has(column.id),
    );

    const columns = [...defaultColumns, ...additionalColumns];

    const reducedIds = columns.reduce(
      (acc, column) => {
        if (isValidColumnId(column.id)) {
          acc[column.id] = column.id;
        }

        return acc;
      },
      {} as typeof defaultIds,
    );

    const csvColumns = [
      ...defaultCsvColumns,
      ...additionalColumns.map(column => convertToCsvColumn(column)),
    ];

    return { columns, ids: reducedIds, csvColumns };
  }, [ids]);

  return result;
};
export default useAggregatedColumns;
